import { createFileRoute, redirect } from '@tanstack/react-router'
import { redirectIfUnauthenticated, getRoleBasedPath, REDIRECT } from '@/lib/auth-redirect'
import { getCompany } from '@/services/company'
import { useCompanyStore } from '@/stores'
import { CompanySelectionPage } from '@/components/auth/company-selection-page'

export const Route = createFileRoute('/start/')({
  beforeLoad: async ({ location }) => {
    redirectIfUnauthenticated({ location })

    const redirectParam = new URLSearchParams(location.search ?? '').get('redirect')
    // Ignore a redirect that points back at this page. Signing in from /start sends
    // the user to /login?redirect=/start, so on the way back this would bounce
    // /start to /start -- the router reported it as a "/start//start/" match and the
    // page never settled.
    if (redirectParam && !redirectParam.replace(/\/+$/, '').endsWith('/start')) {
      throw redirect({ to: redirectParam })
    }

    const { selectedCompanyId, rememberSelection } = useCompanyStore.getState()
    if (!rememberSelection || selectedCompanyId === null) {
      return
    }

    try {
      const companiesRes = await getCompany()
      const acceptedCompanies = (companiesRes?.data ?? []).filter(
        (c) => c.employee_status === 3
      )
      const company = acceptedCompanies.find((c) => c.id === selectedCompanyId)
      if (company) {
        throw redirect({ to: getRoleBasedPath(company.company_role_id) })
      }
    } catch (error: unknown) {
      // If it's a redirect, re-throw it
      if (error && typeof error === 'object' && 'isRedirect' in error) {
        throw error
      }
      // If token is expired/invalid (401), redirect to login
      const errObj = error as Record<string, unknown>
      if (errObj?.status === 401 || errObj?.code === 401) {
        throw redirect({
          to: REDIRECT.login,
          search: { redirect: location.href },
        })
      }
      // Anything else means we simply could not resolve the remembered company --
      // most often because there is nothing to resolve: the company list endpoint
      // answers "no data" for someone who belongs to no company at all, which is a
      // perfectly ordinary state for a new account, not a failure. Throwing here
      // replaced the whole page with "Something went wrong" and left the user with
      // no way forward. The selection page below already copes with an empty or
      // failed list -- it shows the create / join / continue-free choices and a
      // Retry link -- so fall through and let it do that.
      return
    }
  },
  component: CompanySelectionPage,
})
